(function carouselControllerFactory(root) {
  'use strict';

  const EDGE_TOLERANCE = 8;

  function getCarouselState(track) {
    if (!track) {
      return {
        hasOverflow: false,
        canScrollPrevious: false,
        canScrollNext: false,
      };
    }

    const maximumScroll = Math.max(0, Number(track.scrollWidth) - Number(track.clientWidth));
    const currentScroll = Math.max(0, Number(track.scrollLeft) || 0);
    const hasOverflow = maximumScroll > EDGE_TOLERANCE;

    return {
      hasOverflow,
      canScrollPrevious: hasOverflow && currentScroll > EDGE_TOLERANCE,
      canScrollNext: hasOverflow && currentScroll < maximumScroll - EDGE_TOLERANCE,
    };
  }

  function registerCarouselController() {
    if (!root.document || !root.Alpine) {
      return;
    }

    root.Alpine.data('scrollCarousel', (config = {}) => ({
      track: null,
      hasOverflow: false,
      canScrollPrevious: false,
      canScrollNext: false,
      scrollHandler: null,
      resizeObserver: null,
      mutationObserver: null,

      init() {
        this.$nextTick(() => this.connect());
      },

      destroy() {
        if (this.track && this.scrollHandler) {
          this.track.removeEventListener('scroll', this.scrollHandler);
        }

        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
      },

      connect() {
        const trackId = String(config.trackId || '');
        const nextTrack = trackId ? root.document.getElementById(trackId) : null;

        if (this.track && this.scrollHandler) {
          this.track.removeEventListener('scroll', this.scrollHandler);
        }

        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.track = nextTrack;

        if (!this.track) {
          this.update();
          return;
        }

        this.scrollHandler = () => this.update();
        this.track.addEventListener('scroll', this.scrollHandler, { passive: true });

        if (typeof root.ResizeObserver === 'function') {
          this.resizeObserver = new root.ResizeObserver(() => this.update());
          this.resizeObserver.observe(this.track);
          Array.from(this.track.children).forEach((child) => this.resizeObserver.observe(child));
        }

        if (typeof root.MutationObserver === 'function') {
          this.mutationObserver = new root.MutationObserver(() => {
            if (this.resizeObserver) {
              Array.from(this.track.children).forEach((child) => this.resizeObserver.observe(child));
            }
            this.update();
          });
          this.mutationObserver.observe(this.track, { childList: true });
        }

        this.update();
      },

      update() {
        const state = getCarouselState(this.track);
        this.hasOverflow = state.hasOverflow;
        this.canScrollPrevious = state.canScrollPrevious;
        this.canScrollNext = state.canScrollNext;
      },

      scroll(direction) {
        this.update();

        const scrollDirection = Number(direction) < 0 ? -1 : 1;
        const canScroll = scrollDirection < 0 ? this.canScrollPrevious : this.canScrollNext;

        if (!this.track || !canScroll) {
          return;
        }

        const reduceMotion = root.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({
          left: scrollDirection * this.track.clientWidth,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      },
    }));
  }

  if (root.document) {
    root.document.addEventListener('alpine:init', registerCarouselController);

    if (root.Alpine) {
      registerCarouselController();
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      EDGE_TOLERANCE,
      getCarouselState,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
